import { ReactElement, useRef, useState } from 'react';
import CustomDropdown from '../../Shared/DropDowns/CustomDropdown';
import ChevronIcon from '../../../components/svg/ChevronDownIcon';
import MultiSelectForm, { MultiSelectOption } from '../../Shared/Forms/MultiSelectForm';
import { CommitteeSummary } from '@enview/interface/types/Committee';
import { isEqual } from 'lodash-es';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';

type JursidictionCommitteeDropdownProps = {
  selectedCommittees: string[];
  setSelectedCommittees: (arg: string[]) => void;
  availableCommittees: CommitteeSummary[];
  setSearchText: (arg: string) => void;
  searchText: string;
  isSingleJurisdiction?: boolean;
};

const CommitteeDropdown = (props: JursidictionCommitteeDropdownProps): ReactElement => {
  const {
    selectedCommittees,
    setSelectedCommittees,
    availableCommittees,
    setSearchText,
    searchText,
    isSingleJurisdiction = true,
  } = props;
  const [showDropdown, setShowDropdown] = useState(false);
  const [committees, setCommittees] = useState<MultiSelectOption<string>[]>([]);

  const wrapperElemRef = useRef<HTMLDivElement>(null);
  const elementWidth = wrapperElemRef.current
    ? wrapperElemRef.current.offsetWidth
    : undefined;

  const handleTextChange = (newText: string): void => {
    let suggestions: MultiSelectOption<string>[] = [];
    if (newText.length > 0) {
      suggestions = availableCommittees
        .filter((v: CommitteeSummary) =>
          v.name.toLowerCase().includes(newText.toLowerCase()),
        )
        .map((v: CommitteeSummary) => ({
          value: v.name.toLowerCase(),
          label: isSingleJurisdiction
            ? v.name
            : `${v.name} (${v.jurisdiction.abbreviation.toUpperCase()})`,
        }));
      setShowDropdown(true);
    }

    if (newText.length === 0) {
      suggestions = availableCommittees.map((v) => {
        return {
          value: v.name.toLowerCase(),
          label: isSingleJurisdiction
            ? v.name
            : `${v.name} (${v.jurisdiction.abbreviation.toUpperCase()})`,
        };
      });
    }

    setCommittees(suggestions);
    setSearchText(newText);
  };

  const toggleButton = (selectedCount: number): ReactElement => {
    return (
      <button
        className="d-flex btn btn-text ml-auto align-items-center"
        id="legislature-dropdown"
        onClick={() => setShowDropdown(!showDropdown)}
        type="button"
      >
        {selectedCount !== undefined && selectedCount > 0 && (
          <div className="dropdown-count">{selectedCount}</div>
        )}
        <ChevronIcon />
      </button>
    );
  };

  const handleCommitteeChange = (updatedCommittees: string[]): void => {
    if (isEqual(updatedCommittees, selectedCommittees)) {
      return;
    }

    setSelectedCommittees(updatedCommittees);
    handleTextChange('');
  };

  const buildCommitteeOptions = (): MultiSelectOption<string>[] => {
    const selectedOptions: MultiSelectOption<string>[] = [];
    const unselectionOptions: MultiSelectOption<string>[] = [];

    const opts =
      committees.length === 0 && searchText.length === 0
        ? availableCommittees.map((c) => ({
            value: c.name.toLowerCase(),
            label: isSingleJurisdiction
              ? c.name
              : `${c.name} (${c.jurisdiction.abbreviation.toUpperCase()})`,
          }))
        : committees;

    opts.forEach((committee) => {
      const include = selectedCommittees.find((i) => i === committee.value);

      if (include) {
        selectedOptions.push(committee);
      } else {
        unselectionOptions.push(committee);
      }
    });

    return selectedOptions.concat(unselectionOptions);
  };
  const { t } = useTranslation();

  return (
    <div className="jurisdiction-dropdown committee-dropdown" ref={wrapperElemRef}>
      <div className="row flex-nowrap col-4 align-items-center">
        <FontAwesomeIcon className="search-icon pl-2" icon={faSearch} />
        <input
          className="pl-1"
          onChange={(event) => handleTextChange(event.target.value)}
          placeholder={t('hearings.committees.placeholder', {
            count: selectedCommittees.length,
          })}
          type="text"
          value={searchText}
        />

        <CustomDropdown
          alignRight
          setShowDropdown={setShowDropdown}
          showDropdown={showDropdown}
          target={toggleButton(selectedCommittees.length)}
          width={elementWidth}
        >
          <div className="jurisdiction-dropdown-menu">
            <MultiSelectForm
              currentSelection={selectedCommittees}
              options={buildCommitteeOptions()}
              showClearAll
              showSelectAll
              showSelectButtonsBottom
              singleColumn
              updateSelection={handleCommitteeChange}
            />
          </div>{' '}
        </CustomDropdown>
      </div>
    </div>
  );
};

export default CommitteeDropdown;
